import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { chatDetails, s3Upload } from "../../api/user";
import {
  SOCKET_EV_JOIN,
  SOCKET_EV_RECEIVE_MESSAGE,
  SOCKET_EV_SEND_MESSAGE,
  SocketContext,
} from "../../utils/socketio";
import { markThreadSeen } from "../../utils/unreadBadges";
import { toDate } from "../../utils/timeFormat";

// One conversation's data + transport, shared by the desktop two-panel screen
// and the mobile thread screen so both behave identically (same join/receive
// handling, same send path, same optimistic append).

const generateToken = () => {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let token = "";
  for (let i = 0; i < 14; i++) {
    token += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return token;
};

const deviceInfo = () => {
  const ua = navigator.userAgent;
  let device_name = "Unknown";
  if (ua.includes("Chrome")) device_name = "Chrome";
  else if (ua.includes("Safari")) device_name = "Safari";
  else if (ua.includes("Firefox")) device_name = "Firefox";
  else if (ua.includes("Edge")) device_name = "Edge";
  else if (ua.includes("Opera")) device_name = "Opera";

  let device_type = "W";
  if (/Android/i.test(ua)) device_type = "A";
  else if (/iPhone|iPad|iPod/i.test(ua)) device_type = "I";

  return { device_type, device_name };
};

const VIDEO_RE = /\.(mp4|mov|avi|wmv)$/i;
const UPLOADABLE_RE = /\.(jpg|jpeg|png|svg|mp4|mov|avi|wmv)$/i;
const MAX_UPLOAD_BYTES = 512 * 1024 * 1024;

export default function useChatThread({ receiverId, chatGroupId, gameId } = {}) {
  const socket = useContext(SocketContext);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  // Who the conversation is with, straight from get-chat-detail. The header used
  // to guess this from the first incoming message, which is why a thread with no
  // incoming message showed an avatar and nothing else.
  const [friend, setFriend] = useState(null);
  // The conversation's REAL group id. Opened from a profile we only know the
  // receiver, and the id the Chats list stashed belongs to whichever thread was
  // opened last - marking that one seen cleared the wrong row's dot.
  const [groupId, setGroupId] = useState(chatGroupId ?? null);

  // Keep the identifiers in a ref so the socket callbacks always see the thread
  // that is currently open, without re-subscribing on every render.
  const targetRef = useRef({ receiverId, chatGroupId, gameId });
  targetRef.current = { receiverId, chatGroupId, gameId };

  const target = () => {
    const { receiverId: r, chatGroupId: g } = targetRef.current;
    return r ? { receiver_id: r } : g ? { chat_group_id: g } : null;
  };

  const headers = () => {
    const info = deviceInfo();
    return {
      Authorization: `Bearer ${JSON.parse(
        localStorage.getItem("accessToken")
      )}`,
      "Accept-Language": "en",
      "Device-name": localStorage.getItem("DeviceName") || info.device_name,
      "Device-Type": localStorage.getItem("DeviceType") || info.device_type,
      "Device-Token": localStorage.getItem("DeviceToken") || generateToken(),
      "FCM-Token":
        localStorage.getItem("FCM-WEB") || localStorage.getItem("FCM"),
      "App-Version": "1.2",
      Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  };

  // ---- load history ----
  const load = useCallback(async () => {
    const t = target();
    if (!t) {
      setMessages([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await chatDetails({
        ...t,
        ...(targetRef.current.gameId && { game_id: targetRef.current.gameId }),
      });
      const payload = response?.data?.data || {};
      const list = payload?.data || [];
      setMessages(
        [...list].sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      );
      // Both are additive fields on an existing response, so an older backend
      // simply leaves them undefined and the callers fall back as before.
      if (payload.friend_details) setFriend(payload.friend_details);
      if (payload.chat_group_id != null) setGroupId(payload.chat_group_id);
    } catch (error) {
      console.error("Error loading chat:", error);
    } finally {
      setLoading(false);
    }
  }, [receiverId, chatGroupId, gameId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  // Being in the thread means everything in it has been seen — this is what
  // clears its row dot and lets the header dot go dark. The watermark is the
  // newest message's own timestamp so isThreadUnread compares like with like.
  useEffect(() => {
    if (groupId == null) return;
    const newest = messages.reduce((max, m) => {
      const t = toDate(m?.created_at)?.getTime() || 0;
      return t > max ? t : max;
    }, 0);
    markThreadSeen(groupId, newest || Date.now());
  }, [messages, groupId]);

  // ---- join + receive ----
  const joinChat = useCallback(() => {
    const t = target();
    if (!socket || !t) return;
    socket.emit(
      SOCKET_EV_JOIN,
      {
        method: "post",
        route: "join-chat",
        param: { type: "1", ...t },
        headers: headers(),
      },
      (acknowledgment) => {
        if (acknowledgment?.status === 400) setTimeout(joinChat, 3000);
      }
    );
  }, [socket, receiverId, chatGroupId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!socket) return undefined;
    const onConnect = () => joinChat();
    // The seen-watermark effect above reacts to `messages`, so a received
    // message is marked seen automatically while the thread is open.
    const onReceive = (received) => setMessages((prev) => [...prev, received]);
    socket.on("connect", onConnect);
    socket.on(SOCKET_EV_RECEIVE_MESSAGE, onReceive);
    if (socket.connected) joinChat();
    return () => {
      socket.off("connect", onConnect);
      socket.off(SOCKET_EV_RECEIVE_MESSAGE, onReceive);
    };
  }, [socket, joinChat]);

  // ---- send ----
  const emitMessage = (param) =>
    new Promise((resolve) => {
      if (!socket) {
        resolve(null);
        return;
      }
      socket.emit(
        SOCKET_EV_SEND_MESSAGE,
        {
          method: "post",
          route: "send-message",
          param,
          headers: headers(),
        },
        (acknowledgment) => resolve(acknowledgment)
      );
    });

  const sendText = async (text) => {
    const t = target();
    const body = (text || "").trim();
    if (!body || !t || sending) return;
    setSending(true);
    try {
      const ack = await emitMessage({
        type: 1,
        ...t,
        message: body,
        ...(targetRef.current.gameId && { game_id: targetRef.current.gameId }),
      });
      if (ack) setMessages((prev) => [...prev, ack]);
      else console.error("Error sending message");
    } finally {
      setSending(false);
    }
  };

  // Send an attachment that is ALREADY uploaded (an S3 path). The Flutter
  // in-app webview does its own picking + upload and hands us the path back over
  // the bridge, so it enters here rather than through sendAttachment.
  const sendAttachmentPath = useCallback(async (path) => {
    const t = target();
    if (!path || !t) return;
    setSending(true);
    try {
      const ack = await emitMessage({
        type: VIDEO_RE.test(path) ? 3 : 2,
        ...t,
        message: "",
        attachments: path,
        ...(targetRef.current.gameId && { game_id: targetRef.current.gameId }),
      });
      if (ack) setMessages((prev) => [...prev, ack]);
    } finally {
      setSending(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendAttachment = async (file) => {
    if (!file || !target()) return;
    if (!UPLOADABLE_RE.test(file.name)) {
      toast.error("Choose an image or video.");
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error("File must be 512 MB or smaller.");
      return;
    }
    setSending(true);
    try {
      const response = await s3Upload([
        {
          id: null,
          image: null,
          blob: file,
          preview: URL.createObjectURL(file),
        },
      ]);
      const path = response?.data?.data?.[0];
      if (path) await sendAttachmentPath(path);
    } catch (error) {
      console.error("Error uploading attachment:", error);
    } finally {
      setSending(false);
    }
  };

  // Flutter wrapper: it uploads the picked file itself and fires
  // "myCustomChatEvent" with the resulting path (a string, or an array whose
  // first entry is the path).
  useEffect(() => {
    const onFlutterAttachment = (e) => {
      const picked = e?.detail?.imageChat;
      if (!picked) return;
      sendAttachmentPath(Array.isArray(picked) ? picked[0] : picked);
    };
    window.addEventListener("myCustomChatEvent", onFlutterAttachment);
    return () =>
      window.removeEventListener("myCustomChatEvent", onFlutterAttachment);
  }, [sendAttachmentPath]);

  return {
    messages,
    friend,
    groupId,
    loading,
    sending,
    sendText,
    sendAttachment,
    reload: load,
  };
}
